import React from 'react';
import { Input, DatePicker, InputNumber } from 'antd';
import dayjs from 'dayjs';
import { F90B } from '../../../services/pw9201Api';
import LookupField from '../../../components/LookupField';

interface MasterInfoProps {
  formData: Partial<F90B>;
  names: Record<string, string>;
  isEditing: boolean;
  getField: <K extends keyof F90B>(key: K) => F90B[K] | string;
  setField: <K extends keyof F90B>(key: K, value: F90B[K]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  getFieldError?: (field: string) => string | undefined;
  mode: 'browse' | 'add' | 'edit';
}

const dateToInput = (v: string | null | undefined): dayjs.Dayjs | null => {
  if (!v) return null;
  const d = dayjs(v);
  return d.isValid() ? d : null;
};

const FieldError: React.FC<{ error?: string }> = ({ error }) =>
  error ? <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6, fontWeight: 'normal' }}>{error}</span> : null;

const MasterInfo: React.FC<MasterInfoProps> = ({
  formData, names, isEditing,
  getField, setField, resolveSingleName, getFieldError, mode,
}) => {
  const E = (field: string) => <FieldError error={getFieldError?.(field)} />;

  return (
    <div className="pw9201-master-info">
      {/* Header row */}
      <div className="pw9201-header-row">
        <div className="pw9201-field-group">
          <label>申請單號</label>
          <Input
            style={{ width: 140 }}
            readOnly
            value={getField('f90b_01') as string}
            placeholder="（自動產生）"
          />
        </div>

        <div className="pw9201-field-group">
          <label className="required">申請部門{E('f90b_11')}</label>
          <div className="pw9201-lookup-field">
            <LookupField
              lookupType="department"
              value={(getField('f90b_11') as string) || ''}
              onChange={(code, name) => {
                setField('f90b_11', code);
                if (name) resolveSingleName('department', 'department', code);
              }}
              disabled={!isEditing}
              style={{ width: 100 }}
              title="搜尋部門"
            />
            <span className="pw9201-lookup-name">{names.department || '--'}</span>
          </div>
        </div>

        <div className="pw9201-field-group">
          <label className="required">申請人{E('f90b_12')}</label>
          <div className="pw9201-lookup-field">
            <LookupField
              lookupType="employee"
              value={(getField('f90b_12') as string) || ''}
              onChange={(code, name) => {
                setField('f90b_12', code);
                if (name) resolveSingleName('employee', 'employee', code);
              }}
              disabled={!isEditing}
              style={{ width: 120 }}
              title="搜尋員工"
            />
            <span className="pw9201-lookup-name">{names.employee || '--'}</span>
          </div>
        </div>

        <div className="pw9201-field-group">
          <label className="required">申請日期{E('f90b_13')}</label>
          <DatePicker
            style={{ width: 140 }}
            disabled={!isEditing}
            value={dateToInput(formData.f90b_13)}
            onChange={(_d, ds) => {
              const dateStr = typeof ds === 'string' ? ds : ds[0];
              setField('f90b_13', dateStr ? `${dateStr}T00:00:00Z` : (null as unknown as string));
            }}
          />
        </div>

        <div className="pw9201-field-group">
          <label>增補人數</label>
          <InputNumber
            style={{ width: 80 }}
            readOnly
            value={formData.f90b_20 ?? 0}
          />
        </div>
      </div>

      {/* Approval status display (read-only) */}
      {mode === 'browse' && formData.f90b_61 && (
        <div className="pw9201-approval-row">
          <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
            审核状态: {formData.f90b_61 || '--'}
          </span>
        </div>
      )}
    </div>
  );
};

export default React.memo(MasterInfo);
